import { useState, useEffect } from "react";

import {
  CITIZEN_CARD_LENGTH,
  ID_DOCUMENT_MAX_LENGTH,
  isValidIban,
  isValidIdDocumentNumber,
  isValidNif,
  isValidNiss,
  NIF_LENGTH,
  NISS_LENGTH,
  parseDateOnly,
} from "../../../utils/userValidators";
import { ID_DOCUMENT_TYPE } from "../../../utils/constants/user";

const shallowEqual = (a, b) => {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
};

const validate = (data = {}) => {
  const errs = {};

  if (!data.first_name || !data.first_name.trim())
    errs.first_name = "First name is required";

  if (!data.last_name || !data.last_name.trim())
    errs.last_name = "Last name is required";

  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errs.email = "Invalid email format";
  }
  if (!data.email || !data.email.trim()) errs.email = "Email is required";

  if (data.phone_number && !/^\+?\d*$/.test(data.phone_number))
    errs.phone_number = "Phone number must contain only digits";
  if (
    data.emergency_contact_phone &&
    !/^\+?\d*$/.test(data.emergency_contact_phone)
  )
    errs.emergency_contact_phone =
      "Emergency contact phone must contain only digits";

  if (data.date_of_birth) {
    const dateOfBirth = parseDateOnly(data.date_of_birth);
    if (!dateOfBirth) {
      errs.date_of_birth = "Enter a valid date of birth";
    } else if (dateOfBirth > new Date()) {
      errs.date_of_birth = "Date of birth cannot be in the future";
    }
  }

  if (data.hiring_date) {
    const hiringDate = parseDateOnly(data.hiring_date);
    if (!hiringDate) {
      errs.hiring_date = "Enter a valid hiring date";
    } else if (hiringDate > new Date()) {
      errs.hiring_date = "Hiring date cannot be in the future";
    }
  }

  if (data.nif && data.nif.length !== NIF_LENGTH)
    errs.nif = "NIF must have exactly 9 numbers";
  else if (data.nif && !isValidNif(data.nif))
    errs.nif = "Enter a valid NIF";

  if (data.iban && !isValidIban(data.iban))
    errs.iban = "Enter a valid IBAN";

  if (data.cc && !isValidIdDocumentNumber(data.cc, data.id_document_type)) {
    errs.cc =
      data.id_document_type === ID_DOCUMENT_TYPE.CITIZEN_CARD
        ? "Enter a valid Citizen Card number (e.g. 123456789ZZ1)"
        : "Document number must have between 5 and 20 letters or digits";
  }

  if (data.social_security_number && !isValidNiss(data.social_security_number))
    errs.social_security_number =
      "Social Security Number must have exactly 11 digits";

  if (!data.username || !data.username.trim())
    errs.username = "Name is required";
  else if (data.username.trim().length < 3)
    errs.username = "Name must have at least 3 characters";

  if (
    data.postal_code &&
    data.country === "Portugal" &&
    !/^\d{4}-\d{3}$/.test(data.postal_code)
  )
    errs.postal_code = "Postal code must follow the format 0000-000";

  if (
    (data.contract_type === "Fixed Term" || data.contract_type === "Internship") &&
    data.contract_end_date
  ) {
    const contractEndDate = parseDateOnly(data.contract_end_date);
    const hiringDate = parseDateOnly(data.hiring_date);
    if (!contractEndDate) {
      errs.contract_end_date = "Enter a valid contract end date";
    } else if (hiringDate && contractEndDate <= hiringDate) {
      errs.contract_end_date = "End date must be after the hiring date";
    }
  }

  if (data.id_document_expiration_date) {
    const expirationDate = parseDateOnly(data.id_document_expiration_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!expirationDate) {
      errs.id_document_expiration_date = "Enter a valid expiration date";
    } else if (expirationDate < today) {
      errs.id_document_expiration_date = "ID document expiration date cannot be in the past";
    }
  }

  if (data.salary !== "" && data.salary !== null && data.salary !== undefined) {
    const salaryNum = Number(data.salary);
    if (Number.isNaN(salaryNum) || salaryNum < 0) {
      errs.salary = "Salary must be a positive number";
    }
  }

  return errs;
};

const useEmployeeValidation = (initialData = {}, watchKey) => {
  const [form, setForm] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState(initialData);

  useEffect(() => {
    if (watchKey === undefined) {
      return;
    }
    if (initialSnapshot[watchKey] !== initialData[watchKey]) {
      setForm(initialData);
      setErrors({});
      setTouched({});
      setInitialSnapshot(initialData);
    }
  }, [initialData, initialSnapshot, watchKey]);

  useEffect(() => {
    setErrors(validate(form));
  }, [form]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === "nif") {
      const digits = value.replace(/\D/g, "").slice(0, NIF_LENGTH);
      setForm((prev) => ({ ...prev, nif: digits }));
      return;
    }

    if (name === "cc") {
      const maxLength =
        form.id_document_type === ID_DOCUMENT_TYPE.CITIZEN_CARD ? CITIZEN_CARD_LENGTH : ID_DOCUMENT_MAX_LENGTH;
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, maxLength);
      setForm((prev) => ({ ...prev, cc: cleaned }));
      return;
    }

    if (name === "iban") {
      const iban = value
        .replace(/[^A-Z0-9]/gi, "")
        .toUpperCase()
        .slice(0, 34);
      setForm((prev) => ({ ...prev, iban }));
      return;
    }

    if (
      name === "date_of_birth" ||
      name === "hiring_date" ||
      name === "contract_end_date" ||
      name === "id_document_expiration_date"
    ) {
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === "social_security_number") {
      const digits = value.replace(/\D/g, "").slice(0, NISS_LENGTH);
      setForm((prev) => ({ ...prev, [name]: digits }));
      return;
    }

    if (name === "salary") {
      const cleaned = value.replace(/[^\d.,]/g, "").replace(",", ".");
      setForm((prev) => ({ ...prev, salary: cleaned }));
      return;
    }

    if (name === "contract_type") {
      setForm((prev) => ({
        ...prev,
        contract_type: value,
        contract_end_date: value === "Open Ended" ? "" : prev.contract_end_date,
      }));
      return;
    }

    if (name === "id_document_type") {
      setForm((prev) => ({ ...prev, id_document_type: value, cc: "" }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const isChanged = !shallowEqual(form, initialSnapshot);
  const isValid = Object.keys(errors).length === 0 && isChanged;

  return {
    form,
    setForm,
    errors,
    setErrors,
    touched,
    setTouched,
    handleInputChange,
    isValid,
  };
};

export { validate as validateEmployeeData };
export default useEmployeeValidation;